import {
  clientRouteOwnerOwnsHref,
  isAllowedClientRouteOwnerChild,
} from "./client-route-owner-rules";
import { normalizeClientNavigationHref, normalizeClientNavigationPath } from "./client-route-family";
import type { ClientNavTransaction } from "./client-navigation-types";

const MAX_ROUTE_OWNERS = 50;

export type RouteOwnerShadowDecisionKind =
  | "allow-commit"
  | "suppress-commit"
  | "reassert-owner"
  | "drop-restore";

export type RouteOwnerShadowReason =
  | "no-owner"
  | "target-owned-by-current-owner"
  | "allowed-child-route"
  | "newer-owner-protects-current-route"
  | "active-workout-home-commit"
  | "active-workout-program-commit"
  | "stale-restore-after-explicit-owner"
  | "client-home-entry-owner"
  | "inactive-owner-commit"
  | "same-target-rendered-route-split-brain"
  | "same-target-already-current"
  | "unowned-route-commit";

export type RouteOwnerRecord = ClientNavTransaction & {
  startedAtMs: number;
  consumedAtMs: number | null;
  expiredAtMs: number | null;
};

export type RouteOwnerShadowDecision = {
  kind: RouteOwnerShadowDecisionKind;
  reason: RouteOwnerShadowReason;
  ownerId: string | null;
  ownerHref: string | null;
  ownerSource: string | null;
  targetHref: string | null;
  currentHref: string | null;
};

export type RouteOwnerMachineState = {
  owners: RouteOwnerRecord[];
  currentOwnerId: string | null;
  currentHref: string;
  latestDecision: RouteOwnerShadowDecision | null;
  decisionCount: number;
};

export type RouteOwnerMachineEvent =
  | {
      type: "transaction-recorded";
      atMs: number;
      transaction: ClientNavTransaction;
    }
  | {
      type: "owner-consumed";
      atMs: number;
      transactionId: string;
      reason: string;
    }
  | {
      type: "owner-expired";
      atMs: number;
      transactionId: string;
      reason: string;
    }
  | {
      type: "history-commit-observed";
      atMs: number;
      historyEvent: "pushState" | "replaceState" | "popstate";
      transactionId?: string | null;
      targetHref: string;
      currentHrefBeforeCommit: string;
    }
  | {
      type: "navigation-request-observed";
      atMs: number;
      transactionId?: string | null;
      targetHref: string;
      currentHref: string;
      renderedCurrentPath?: string | null;
    }
  | {
      type: "route-observed";
      atMs: number;
      href: string;
    }
  | {
      type: "saved-route-restore-observed";
      atMs: number;
      targetHref: string;
      currentHrefBeforeCommit: string;
      navigationType?: string | null;
    }
  | {
      type: "same-target-rendered-route-observed";
      atMs: number;
      transactionId?: string | null;
      targetHref: string;
      currentHref: string;
      renderedCurrentPath: string;
    };

export function createRouteOwnerMachineState(currentHref = "/client"): RouteOwnerMachineState {
  return {
    owners: [],
    currentOwnerId: null,
    currentHref: normalizeClientNavigationHref(currentHref),
    latestDecision: null,
    decisionCount: 0,
  };
}

function toRecord(transaction: ClientNavTransaction): RouteOwnerRecord {
  const startedAtMs = Date.parse(transaction.initiatedAtIso);
  return {
    ...transaction,
    to: normalizeClientNavigationHref(transaction.to),
    toPath: normalizeClientNavigationPath(transaction.toPath),
    fromPath: normalizeClientNavigationPath(transaction.fromPath),
    startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : 0,
    consumedAtMs: transaction.consumedAtIso ? Date.parse(transaction.consumedAtIso) : null,
    expiredAtMs: transaction.expiredAtIso ? Date.parse(transaction.expiredAtIso) : null,
  };
}

function isExpired(owner: RouteOwnerRecord): boolean {
  return owner.expiredAtMs !== null || Boolean(owner.expiredAtIso);
}

function isConsumed(owner: RouteOwnerRecord): boolean {
  return owner.consumedAtMs !== null || Boolean(owner.consumedAtIso);
}

function isExplicitOwner(owner: RouteOwnerRecord): boolean {
  return owner.accepted === true && owner.reason !== "restore" && owner.reason !== "guard-correction";
}

function isWorkoutOwner(owner: RouteOwnerRecord): boolean {
  return owner.toPath === "/client/workout" && owner.accepted === true && !isExpired(owner);
}

function latestLiveOwner(owners: RouteOwnerRecord[]): RouteOwnerRecord | null {
  for (let index = owners.length - 1; index >= 0; index -= 1) {
    const owner = owners[index];
    if (owner.accepted && !isExpired(owner)) return owner;
  }
  return null;
}

function latestExplicitOwner(owners: RouteOwnerRecord[]): RouteOwnerRecord | null {
  for (let index = owners.length - 1; index >= 0; index -= 1) {
    const owner = owners[index];
    if (isExplicitOwner(owner) && !isExpired(owner)) return owner;
  }
  return null;
}

function latestOwnerForHref(owners: RouteOwnerRecord[], href: string): RouteOwnerRecord | null {
  for (let index = owners.length - 1; index >= 0; index -= 1) {
    const owner = owners[index];
    if (owner.accepted && !isExpired(owner) && clientRouteOwnerOwnsHref(owner, href)) return owner;
  }
  return null;
}

function latestWorkoutOwner(owners: RouteOwnerRecord[]): RouteOwnerRecord | null {
  for (let index = owners.length - 1; index >= 0; index -= 1) {
    const owner = owners[index];
    if (isWorkoutOwner(owner)) return owner;
  }
  return null;
}

function findOwnerById(owners: RouteOwnerRecord[], transactionId: string | null | undefined): RouteOwnerRecord | null {
  if (!transactionId) return null;
  return owners.find((owner) => owner.transactionId === transactionId) ?? null;
}

function withDecision(
  state: RouteOwnerMachineState,
  decision: RouteOwnerShadowDecision
): { state: RouteOwnerMachineState; decision: RouteOwnerShadowDecision } {
  return {
    state: {
      ...state,
      currentHref: decision.kind === "allow-commit" && decision.targetHref ? decision.targetHref : state.currentHref,
      latestDecision: decision,
      decisionCount: state.decisionCount + 1,
    },
    decision,
  };
}

function decision(params: {
  kind: RouteOwnerShadowDecisionKind;
  reason: RouteOwnerShadowReason;
  owner: RouteOwnerRecord | null;
  targetHref: string | null;
  currentHref: string | null;
}): RouteOwnerShadowDecision {
  return {
    kind: params.kind,
    reason: params.reason,
    ownerId: params.owner?.transactionId ?? null,
    ownerHref: params.owner?.to ?? null,
    ownerSource: params.owner?.source ?? null,
    targetHref: params.targetHref,
    currentHref: params.currentHref,
  };
}

function reduceHistoryCommit(
  state: RouteOwnerMachineState,
  event: Extract<RouteOwnerMachineEvent, { type: "history-commit-observed" }>
): { state: RouteOwnerMachineState; decision: RouteOwnerShadowDecision } {
  const targetHref = normalizeClientNavigationHref(event.targetHref);
  const currentHref = normalizeClientNavigationHref(event.currentHrefBeforeCommit);
  const currentPath = normalizeClientNavigationPath(currentHref);
  const targetPath = normalizeClientNavigationPath(targetHref);
  const eventOwner = findOwnerById(state.owners, event.transactionId);
  const currentOwner = latestOwnerForHref(state.owners, currentHref) ?? latestLiveOwner(state.owners);

  if (eventOwner && (isExpired(eventOwner) || isConsumed(eventOwner))) {
    return withDecision(
      state,
      decision({
        kind: "suppress-commit",
        reason: "inactive-owner-commit",
        owner: eventOwner,
        targetHref,
        currentHref,
      })
    );
  }

  if (eventOwner && !isExpired(eventOwner) && !isConsumed(eventOwner) && clientRouteOwnerOwnsHref(eventOwner, targetHref)) {
    return withDecision(
      { ...state, currentOwnerId: eventOwner.transactionId },
      decision({
        kind: "allow-commit",
        reason: "target-owned-by-current-owner",
        owner: eventOwner,
        targetHref,
        currentHref,
      })
    );
  }

  if (!currentOwner) {
    return withDecision(
      state,
      decision({ kind: "allow-commit", reason: "no-owner", owner: null, targetHref, currentHref })
    );
  }

  if (clientRouteOwnerOwnsHref(currentOwner, targetHref)) {
    return withDecision(
      { ...state, currentOwnerId: currentOwner.transactionId },
      decision({
        kind: "allow-commit",
        reason: "target-owned-by-current-owner",
        owner: currentOwner,
        targetHref,
        currentHref,
      })
    );
  }

  if (
    isAllowedClientRouteOwnerChild({
      owner: currentOwner,
      currentHref,
      targetHref,
    })
  ) {
    return withDecision(
      { ...state, currentOwnerId: currentOwner.transactionId },
      decision({ kind: "allow-commit", reason: "allowed-child-route", owner: currentOwner, targetHref, currentHref })
    );
  }

  const workoutOwner = latestWorkoutOwner(state.owners);
  if (
    workoutOwner &&
    currentPath === "/client/workout" &&
    (targetPath === "/client" || targetPath === "/client/program")
  ) {
    return withDecision(
      { ...state, currentOwnerId: workoutOwner.transactionId },
      decision({
        kind: "reassert-owner",
        reason: targetPath === "/client/program" ? "active-workout-program-commit" : "active-workout-home-commit",
        owner: workoutOwner,
        targetHref,
        currentHref,
      })
    );
  }

  if (clientRouteOwnerOwnsHref(currentOwner, currentHref)) {
    return withDecision(
      { ...state, currentOwnerId: currentOwner.transactionId },
      decision({
        kind: "suppress-commit",
        reason: "newer-owner-protects-current-route",
        owner: currentOwner,
        targetHref,
        currentHref,
      })
    );
  }

  return withDecision(
    state,
    decision({ kind: "allow-commit", reason: "unowned-route-commit", owner: currentOwner, targetHref, currentHref })
  );
}

function reduceRestore(
  state: RouteOwnerMachineState,
  event: Extract<RouteOwnerMachineEvent, { type: "saved-route-restore-observed" }>
): { state: RouteOwnerMachineState; decision: RouteOwnerShadowDecision } {
  const targetHref = normalizeClientNavigationHref(event.targetHref);
  const currentHref = normalizeClientNavigationHref(event.currentHrefBeforeCommit);
  const targetPath = normalizeClientNavigationPath(targetHref);
  const currentPath = normalizeClientNavigationPath(currentHref);
  const explicitOwner = latestExplicitOwner(state.owners);

  if (explicitOwner && event.atMs - explicitOwner.startedAtMs <= 8000) {
    return withDecision(
      state,
      decision({
        kind: "drop-restore",
        reason: "stale-restore-after-explicit-owner",
        owner: explicitOwner,
        targetHref,
        currentHref,
      })
    );
  }

  const hasRestoreNavigationEvidence = event.navigationType === "reload" || event.navigationType === "back_forward";
  if (currentPath === "/client" && targetPath.startsWith("/client/") && !hasRestoreNavigationEvidence) {
    return withDecision(
      state,
      decision({
        kind: "drop-restore",
        reason: "client-home-entry-owner",
        owner: null,
        targetHref,
        currentHref,
      })
    );
  }

  return withDecision(
    state,
    decision({ kind: "allow-commit", reason: "no-owner", owner: explicitOwner, targetHref, currentHref })
  );
}

function reduceSameTargetRenderedRoute(
  state: RouteOwnerMachineState,
  event: Extract<RouteOwnerMachineEvent, { type: "same-target-rendered-route-observed" }>
): { state: RouteOwnerMachineState; decision: RouteOwnerShadowDecision } {
  const targetHref = normalizeClientNavigationHref(event.targetHref);
  const currentHref = normalizeClientNavigationHref(event.currentHref);
  const targetPath = normalizeClientNavigationPath(targetHref);
  const renderedCurrentPath = normalizeClientNavigationPath(event.renderedCurrentPath);
  const owner = findOwnerById(state.owners, event.transactionId) ?? latestOwnerForHref(state.owners, targetHref);

  if (currentHref === targetHref && renderedCurrentPath !== targetPath && owner) {
    return withDecision(
      { ...state, currentOwnerId: owner.transactionId },
      decision({
        kind: "reassert-owner",
        reason: "same-target-rendered-route-split-brain",
        owner,
        targetHref,
        currentHref,
      })
    );
  }

  return withDecision(
    state,
    decision({
      kind: "allow-commit",
      reason: owner ? "target-owned-by-current-owner" : "no-owner",
      owner,
      targetHref,
      currentHref,
    })
  );
}

function reduceNavigationRequest(
  state: RouteOwnerMachineState,
  event: Extract<RouteOwnerMachineEvent, { type: "navigation-request-observed" }>
): { state: RouteOwnerMachineState; decision: RouteOwnerShadowDecision } {
  const targetHref = normalizeClientNavigationHref(event.targetHref);
  const currentHref = normalizeClientNavigationHref(event.currentHref);
  const targetPath = normalizeClientNavigationPath(targetHref);
  const renderedCurrentPath = event.renderedCurrentPath
    ? normalizeClientNavigationPath(event.renderedCurrentPath)
    : null;
  const eventOwner = findOwnerById(state.owners, event.transactionId);
  const targetOwner = eventOwner ?? latestOwnerForHref(state.owners, targetHref);
  const currentOwner = latestOwnerForHref(state.owners, currentHref) ?? latestLiveOwner(state.owners);

  if (currentHref === targetHref) {
    if (renderedCurrentPath && renderedCurrentPath !== targetPath && targetOwner) {
      return withDecision(
        { ...state, currentOwnerId: targetOwner.transactionId },
        decision({
          kind: "reassert-owner",
          reason: "same-target-rendered-route-split-brain",
          owner: targetOwner,
          targetHref,
          currentHref,
        })
      );
    }

    return withDecision(
      { ...state, currentOwnerId: targetOwner?.transactionId ?? state.currentOwnerId },
      decision({
        kind: "suppress-commit",
        reason: "same-target-already-current",
        owner: targetOwner,
        targetHref,
        currentHref,
      })
    );
  }

  if (targetOwner && clientRouteOwnerOwnsHref(targetOwner, targetHref)) {
    return withDecision(
      { ...state, currentOwnerId: targetOwner.transactionId },
      decision({
        kind: "allow-commit",
        reason: "target-owned-by-current-owner",
        owner: targetOwner,
        targetHref,
        currentHref,
      })
    );
  }

  if (currentOwner && clientRouteOwnerOwnsHref(currentOwner, currentHref)) {
    return withDecision(
      { ...state, currentOwnerId: currentOwner.transactionId },
      decision({
        kind: "suppress-commit",
        reason: "newer-owner-protects-current-route",
        owner: currentOwner,
        targetHref,
        currentHref,
      })
    );
  }

  return withDecision(
    state,
    decision({ kind: "allow-commit", reason: "no-owner", owner: null, targetHref, currentHref })
  );
}

function reduceRouteObserved(
  state: RouteOwnerMachineState,
  event: Extract<RouteOwnerMachineEvent, { type: "route-observed" }>
): { state: RouteOwnerMachineState; decision: RouteOwnerShadowDecision } {
  const href = normalizeClientNavigationHref(event.href);
  const owner = latestOwnerForHref(state.owners, href);

  return withDecision(
    {
      ...state,
      currentHref: href,
      currentOwnerId: owner?.transactionId ?? null,
    },
    decision({
      kind: "allow-commit",
      reason: owner ? "target-owned-by-current-owner" : "no-owner",
      owner,
      targetHref: href,
      currentHref: href,
    })
  );
}

export function reduceRouteOwnerMachine(
  state: RouteOwnerMachineState,
  event: RouteOwnerMachineEvent
): { state: RouteOwnerMachineState; decision: RouteOwnerShadowDecision | null } {
  if (event.type === "transaction-recorded") {
    const record = toRecord(event.transaction);
    const owners = [
      ...state.owners.filter((owner) => owner.transactionId !== record.transactionId),
      record,
    ].slice(-MAX_ROUTE_OWNERS);
    return {
      state: {
        ...state,
        owners,
        currentOwnerId: record.accepted ? record.transactionId : state.currentOwnerId,
      },
      decision: null,
    };
  }

  if (event.type === "owner-consumed" || event.type === "owner-expired") {
    const owners = state.owners.map((owner) => {
      if (owner.transactionId !== event.transactionId) return owner;
      if (event.type === "owner-consumed") {
        return {
          ...owner,
          consumedAtIso: owner.consumedAtIso ?? new Date(event.atMs).toISOString(),
          consumedReason: owner.consumedReason ?? event.reason,
          consumedAtMs: owner.consumedAtMs ?? event.atMs,
        };
      }
      return {
        ...owner,
        expiredAtIso: owner.expiredAtIso ?? new Date(event.atMs).toISOString(),
        expiredAtMs: owner.expiredAtMs ?? event.atMs,
      };
    });
    return { state: { ...state, owners }, decision: null };
  }

  if (event.type === "history-commit-observed") {
    return reduceHistoryCommit(state, event);
  }

  if (event.type === "navigation-request-observed") {
    return reduceNavigationRequest(state, event);
  }

  if (event.type === "route-observed") {
    return reduceRouteObserved(state, event);
  }

  if (event.type === "same-target-rendered-route-observed") {
    return reduceSameTargetRenderedRoute(state, event);
  }

  return reduceRestore(state, event);
}
